// components/AdminPortal/CreateSkillMappingModal.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Layer,
  Heading,
  TextInput,
  Select,
  Text,
  Button,
  Spinner,
  CheckBox,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
  Notification,
} from "grommet";
import adminAxios from "../../config/adminAxios";

const CreateSkillMappingModal = ({
  open,
  onClose,
  portfolioId,
  allCourses,
  onCreated,
}) => {
  const [skillName, setSkillName] = useState("");
  const [oem, setOem] = useState("");
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [allQuestions, setAllQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [showSummary, setShowSummary] = useState(false);
  const [showToast, setShowToast] = useState(false);

  const vendorOptions = [
    { label: "HPE", value: "HPE" },
    { label: "SUSE", value: "SUSE" },
    { label: "Red Hat", value: "Red Hat" },
    { label: "PRINCE", value: "PRINCE" },
    { label: "OpsRamp", value: "OpsRamp" },
    { label: "NVidia", value: "NVidia" },
    { label: "NIST", value: "NIST" },
    { label: "Morpheus", value: "morpheus" },
    { label: "Microsoft", value: "Microsoft" },
    { label: "Meteoros", value: "Met" },
    { label: "ITIL", value: "ITIL" },
    { label: "CompTIA", value: "CompTIA" },
    { label: "Other", value: "Other" },
  ];

  useEffect(() => {
    if (!open) return;
    setLoadingQuestions(true);
    adminAxios
      .get(`/api/admin/questions-by-portfolio/${portfolioId}`)
      .then((res) => setAllQuestions(res.data.questions || []))
      .catch(console.error)
      .finally(() => setLoadingQuestions(false));
  }, [open, portfolioId]);

  const filteredQuestions = allQuestions.filter((q) =>
    selectedCourses.some((c) => c.course_id === q.course_id),
  );

  const toggleQuestion = (q) => {
    setSelectedQuestions((prev) =>
      prev.some((x) => x.question_id === q.question_id)
        ? prev.filter((x) => x.question_id !== q.question_id)
        : [...prev, q],
    );
  };

  const handleSubmit = () => {
    setShowSummary(true);
  };

  const handleConfirm = async () => {
    const payload = {
      portfolioId,
      skillName,
      oem,
      courseIds: selectedCourses.map((c) => c.course_id),
      questionIds: selectedQuestions.map((q) => q.question_id),
    };
    console.log("Submitting skill mapping payload:", payload);

    try {
      await adminAxios.post("/api/admin/add-skill", payload);
      setShowToast(true);
      onCreated();
      // wait a bit so the toast is visible, then close + reload
      setTimeout(() => {
        setShowToast(false);
        onClose();
        window.location.reload();
      }, 1500);
    } catch (err) {
      console.error("Error creating skill mapping:", err);
    }
  };

  if (!open) return null;

  return (
    <Layer onEsc={onClose} onClickOutside={onClose} responsive>
      <Box
        pad="medium"
        width="xlarge"
        round="small"
        background="white"
        gap="small"
      >
        {showToast && (
          <Notification
            toast
            status="normal"
            message="Skill mapping created successfully"
            onClose={() => setShowToast(false)}
          />
        )}

        {!showSummary ? (
          <>
            <Heading level={3} margin={{ bottom: "xsmall" }}>
              Create New Skill Mapping
            </Heading>

            <Box direction="row" gap="small" margin={{ bottom: "xsmall" }}>
              <Box flex>
                <TextInput
                  placeholder="Skill Name"
                  value={skillName}
                  onChange={(e) => setSkillName(e.target.value)}
                />
              </Box>
              <Box flex>
                <Select
                  options={vendorOptions}
                  value={oem}
                  onChange={({ value }) => setOem(value.value)} // Ensure that value is set correctly
                  placeholder="Select Vendor"
                  dropHeight="185px"
                />
              </Box>
              <Box flex>
                <Select
                  placeholder="Select courses"
                  options={allCourses}
                  labelKey="course_name"
                  valueKey="course_id"
                  multiple
                  value={selectedCourses}
                  onChange={({ value }) => {
                    setSelectedCourses(value);
                    setSelectedQuestions([]);
                  }}
                  dropHeight="185px"
                />
              </Box>
            </Box>

            {selectedCourses.length > 0 && (
              <Text size="small" margin={{ vertical: "small" }}>
                <Text weight="bold" as="span">
                  Selected Courses:
                </Text>{" "}
                {selectedCourses.map((c) => c.course_name).join(", ")}
              </Text>
            )}

            <Box
              border={{ color: "light-4" }}
              pad="xsmall"
              margin={{ bottom: "small" }}
              style={{ maxHeight: "60vh", overflowY: "auto" }}
            >
              {loadingQuestions ? (
                <Box align="center" pad="medium">
                  <Spinner />
                </Box>
              ) : selectedCourses.length === 0 ? (
                <Box pad="medium">
                  <Text size="small" color="dark-4" textAlign="center">
                    Select at least one course to load its questions.
                  </Text>
                </Box>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableCell />
                      <TableCell>Question</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Course</TableCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredQuestions.map((q) => (
                      <TableRow key={q.question_id}>
                        <TableCell>
                          <CheckBox
                            checked={selectedQuestions.some(
                              (x) => x.question_id === q.question_id,
                            )}
                            onChange={() => toggleQuestion(q)}
                          />
                        </TableCell>
                        <TableCell>{q.question}</TableCell>
                        <TableCell>
                          {q.weight === 1 ? "Y/N" : "Range"}
                        </TableCell>
                        <TableCell>{q.course_name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>

            {selectedCourses.length > 0 && !loadingQuestions && (
              <Box
                direction="row"
                justify="between"
                pad="xsmall"
                margin={{ bottom: "small" }}
              >
                <Text size="small">
                  Showing {filteredQuestions.length} question
                  {filteredQuestions.length !== 1 ? "s" : ""}
                </Text>
                <Text size="small">
                  Selected {selectedQuestions.length} question
                  {selectedQuestions.length !== 1 ? "s" : ""}
                </Text>
              </Box>
            )}

            <Box direction="row" justify="end" gap="small">
              <Button label="Cancel" onClick={onClose} />
              <Button
                label="Submit"
                primary
                disabled={
                  !skillName ||
                  !oem ||
                  selectedCourses.length === 0 ||
                  selectedQuestions.length === 0
                }
                onClick={handleSubmit}
              />
            </Box>
          </>
        ) : (
          <>
            <Heading level={3} margin={{ bottom: "small" }}>
              Confirm Skill Mapping
            </Heading>

            <Text size="small">
              <Text weight="bold" as="span">
                New Skill Name:
              </Text>{" "}
              {skillName} <Text>| </Text>
              <Text weight="bold" as="span">
                Skill Vendor:
              </Text>{" "}
              {oem}
            </Text>

            <Text size="small" margin={{ top: "small" }}>
              <Text weight="bold" as="span">
                Selected Courses:
              </Text>{" "}
              {selectedCourses.map((c) => c.course_name).join(", ")}
            </Text>

            <Box
              border={{ color: "light-4" }}
              pad="xsmall"
              margin={{ top: "small", bottom: "small" }}
              style={{ maxHeight: "40vh", overflowY: "auto" }}
            >
              {selectedQuestions.map((q) => (
                <Text
                  key={q.question_id}
                  size="small"
                  margin={{ bottom: "xsmall" }}
                >
                  • {q.question} — ( {q.weight === 1 ? "Y/N" : "Range"} |{" "}
                  {q.course_name})
                </Text>
              ))}
            </Box>

            <Box direction="row" justify="end" gap="small">
              <Button label="Back" onClick={() => setShowSummary(false)} />
              <Button label="Confirm" primary onClick={handleConfirm} />
            </Box>
          </>
        )}
      </Box>
    </Layer>
  );
};

export default CreateSkillMappingModal;
